import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Grid, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip,
  CircularProgress, Alert, LinearProgress
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { LOYALTY_API_BASE, SECTION_COLORS } from './constants';
import { useBrand } from '../../contexts/BrandContext';

export default function ProfilFidelite() {
  const navigate = useNavigate();
  const { id: profileId } = useParams();
  const { currentBrand } = useBrand();
  const brandName = currentBrand?.name || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [wallet, setWallet] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [movements, setMovements] = useState([]);
  const [customerRewards, setCustomerRewards] = useState([]);
  const [rewardDefs, setRewardDefs] = useState({});

  useEffect(() => {
    if (!brandName || !profileId) return;

    const fetchAllData = async () => {
      setLoading(true);
      setError(null);
      const headers = { "X-Brand": brandName };

      try {
        // Validate customer exists / fetch wallet
        const wRes = await fetch(`${LOYALTY_API_BASE}/customers/${encodeURIComponent(brandName)}/${encodeURIComponent(profileId)}/wallet`, { headers });
        if (!wRes.ok) {
          if (wRes.status === 404) throw new Error("Profil client introuvable dans le système de fidélité.");
          throw new Error("Erreur de récupération du wallet client.");
        }
        setWallet(await wRes.json());

        // Fetch loyalty (tier details)
        const lRes = await fetch(`${LOYALTY_API_BASE}/customers/${encodeURIComponent(brandName)}/${encodeURIComponent(profileId)}/loyalty`, { headers });
        if (lRes.ok) setLoyalty(await lRes.json());

        // Fetch point movements (Historique)
        const pmRes = await fetch(`${LOYALTY_API_BASE}/customers/${encodeURIComponent(brandName)}/${encodeURIComponent(profileId)}/point-movements`, { headers });
        if (pmRes.ok) setMovements(await pmRes.json());

        // Fetch customer rewards
        const crRes = await fetch(`${LOYALTY_API_BASE}/customers/${encodeURIComponent(brandName)}/${encodeURIComponent(profileId)}/rewards`, { headers });
        let assignedRewards = [];
        if (crRes.ok) assignedRewards = await crRes.json();
        setCustomerRewards(assignedRewards);

        // Map reward IDs to Reward definitions for names
        if (assignedRewards.length > 0) {
          const rRes = await fetch(`${LOYALTY_API_BASE}/rewards?brand=${encodeURIComponent(brandName)}`, { headers });
          if (rRes.ok) {
            const rData = await rRes.json();
            const defMap = {};
            rData.forEach(r => defMap[r.id] = r);
            setRewardDefs(defMap);
          }
        }

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [brandName, profileId]);

  if (!brandName) {
    return (
      <Box p={3} minHeight="100vh" bgcolor="#f5f7fa">
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          Veuillez sélectionner une marque pour consulter ce profil.
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/fidelite')}>Retour au tableau de bord</Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box p={5} display="flex" justifyContent="center" alignItems="center" minHeight="50vh" bgcolor="#f5f7fa">
        <CircularProgress sx={{ color: SECTION_COLORS.tiers.main }} />
      </Box>
    );
  }

  if (error || !wallet) {
    return (
      <Box p={3} minHeight="100vh" bgcolor="#f5f7fa">
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/fidelite')}>Retour</Button>
      </Box>
    );
  }

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'available': return 'success';
      case 'used': return 'default';
      case 'expired': return 'error';
      default: return 'primary';
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case 'available': return 'Disponible';
      case 'used': return 'Utilisé';
      case 'expired': return 'Expiré';
      default: return status;
    }
  };

  return (
    <Box p={3} bgcolor="#f5f7fa" minHeight="100vh">
      <Box maxWidth={1200} mx="auto">
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/fidelite')} sx={{ mb: 3 }}>
          Retour au tableau de bord
        </Button>

        {/* PROFIL HEADER */}
        <Paper sx={{ p: { xs: 2, md: 4 }, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="h4" fontWeight={800} fontFamily="Montserrat, sans-serif" color="#1a1a2e">
                  {profileId}
                </Typography>
                <Typography color="text.secondary" fontWeight={500}>
                  Profil ID • {brandName}
                </Typography>

                {loyalty && loyalty.currentTier && (
                  <Box mt={2}>
                    <Typography variant="body2" color="text.secondary" mb={0.5}>
                      Progression avant le niveau {loyalty.nextTier ? loyalty.nextTier.name : "Maximum"}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <LinearProgress
                        variant="determinate"
                        value={loyalty.nextTier ? Math.min(100, (loyalty.statusPoints / loyalty.nextTier.minStatusPoints) * 100) : 100}
                        sx={{ flexGrow: 1, height: 8, borderRadius: 4, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: SECTION_COLORS.tiers.main } }}
                      />
                      <Typography variant="caption" fontWeight={600} color={SECTION_COLORS.tiers.main}>
                        {loyalty.statusPoints} SP
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Box textAlign="center" p={2} bgcolor="#fafbfd" borderRadius={2} border="1px solid #e8ecf1">
                    <Typography fontSize={12} color="text.secondary" fontWeight={600} mb={0.5}>SOLDE</Typography>
                    <Typography variant="h5" fontWeight={800} color={SECTION_COLORS.tiers.main}>{wallet.pointsBalance}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box textAlign="center" p={2} bgcolor="#fafbfd" borderRadius={2} border="1px solid #e8ecf1">
                    <Typography fontSize={12} color="text.secondary" fontWeight={600} mb={0.5}>NIVEAU</Typography>
                    <Typography variant="h5" fontWeight={800} color="#FFD93D">{loyalty?.currentTier?.name || wallet.loyaltyStatus || '–'}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box textAlign="center" p={2} bgcolor="#fafbfd" borderRadius={2} border="1px solid #e8ecf1">
                    <Typography fontSize={12} color="text.secondary" fontWeight={600} mb={0.5}>À VIE</Typography>
                    <Typography variant="h5" fontWeight={800} color={SECTION_COLORS.rewards.main}>{wallet.lifetimePoints}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>

        {/* REWARDS (AVANTAGES & VOUCHERS) */}
        <Typography variant="h6" fontWeight={700} fontFamily="Montserrat, sans-serif" mb={2} color="#1a1a2e">
          Avantages & Vouchers ({customerRewards.length})
        </Typography>
        <TableContainer component={Paper} elevation={0} sx={{ mb: 4, borderRadius: 3, border: "1px solid #e8ecf1" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9ff' }}>
                <TableCell sx={{ fontWeight: 600 }}>Récompense</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Obtenu le</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Expire le</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Utilisé le</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customerRewards.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    Aucune récompense active.
                  </TableCell>
                </TableRow>
              ) : customerRewards.map((row) => {
                const def = rewardDefs[row.reward_id];
                return (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Typography fontWeight={600} fontSize={14}>{def ? def.name : row.reward_id}</Typography>
                      {def && def.description && <Typography fontSize={12} color="text.secondary">{def.description}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip label={getStatusLabel(row.status)} color={getStatusColor(row.status)} size="small" sx={{ fontWeight: 600, fontSize: 11 }} />
                    </TableCell>
                    <TableCell>{row.issued_at ? new Date(row.issued_at).toLocaleDateString() : '–'}</TableCell>
                    <TableCell>{row.expires_at ? new Date(row.expires_at).toLocaleDateString() : 'Jamais'}</TableCell>
                    <TableCell>{row.used_at ? new Date(row.used_at).toLocaleDateString() : '–'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* POINT MOVEMENTS (HISTORIQUE) */}
        <Typography variant="h6" fontWeight={700} fontFamily="Montserrat, sans-serif" mb={2} color="#1a1a2e">
          Historique des Points ({movements.length})
        </Typography>
        <TableContainer component={Paper} elevation={0} sx={{ mb: 4, borderRadius: 3, border: "1px solid #e8ecf1" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9ff' }}>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Variation</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {movements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    Aucun mouvement enregistré.
                  </TableCell>
                </TableRow>
              ) : movements.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.created_at ? new Date(row.created_at).toLocaleString() : '–'}</TableCell>
                  <TableCell>
                    <Chip label={row.type} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: 11, borderRadius: 1 }} />
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={700} color={row.points > 0 ? 'success.main' : row.points < 0 ? 'error.main' : 'text.secondary'}>
                      {row.points > 0 ? `+${row.points}` : row.points}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

      </Box>
    </Box>
  );
}
